/**
 * The feature-code button: shadcn Button plus variant-by-action and a loading
 * state. For buttons that open another page, pass `navigateTo` instead of
 * `onClick={() => router.push(...)}` so the spinner shows on click and clears
 * once the destination has loaded:
 *
 *   <ActionButton navigateTo={`/manage/courses/${id}/questions`}>Questions</ActionButton>
 */

'use client';

import React from 'react';
import { useTransition } from 'react';
import { useRouter } from 'next/navigation';
import { Button, ButtonProps } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils/utils';

interface ActionButtonProps extends ButtonProps {
  /** Shows a spinner and disables the button */
  loading?: boolean;
  loadingText?: string;
  /** Selects the variant when `variant` is not set */
  action?: 'create' | 'update' | 'delete' | 'submit' | 'cancel';
  /**
   * Destination path; the button manages its own loading state until the
   * destination has loaded. Ignored when `asChild` is set.
   */
  navigateTo?: string;
  /** Use router.replace instead of router.push for `navigateTo`. */
  navigateReplace?: boolean;
}

function getVariantForAction(action?: string): ButtonProps['variant'] {
  switch (action) {
    case 'delete':
      return 'destructive';
    case 'cancel':
      return 'outline';
    case 'create':
    case 'update':
    case 'submit':
    default:
      return 'default';
  }
}

/**
 * Visual button core (no navigation behavior). Kept hook-free so ordinary
 * buttons — and the many tests that render them — never require a router
 * context. Navigation hooks live in NavActionButton below and only run when
 * `navigateTo` is actually used.
 */
const ActionButtonBase = React.forwardRef<HTMLButtonElement, ActionButtonProps>(
  function ActionButtonBase(
    {
      loading = false,
      loadingText,
      action,
      children,
      disabled,
      variant,
      className,
      // navigation props are handled by NavActionButton; never forward them to <Button>
      navigateTo: _navigateTo,
      navigateReplace: _navigateReplace,
      ...props
    },
    ref
  ) {
    const buttonVariant = variant || getVariantForAction(action);

    const isDisabled = disabled || loading;

    const buttonContent = loading && loadingText ? loadingText : children;

    // When asChild is used, Radix Slot expects exactly one child element.
    // Skip the loading wrapper to avoid passing multiple children.
    if (props.asChild) {
      return (
        <Button
          {...props}
          ref={ref}
          variant={buttonVariant}
          disabled={isDisabled}
          className={cn(className)}
        >
          {children}
        </Button>
      );
    }

    return (
      <Button
        {...props}
        ref={ref}
        variant={buttonVariant}
        disabled={isDisabled}
        className={cn(className)}
        aria-busy={loading}
        {...(loading
          ? { 'aria-label': `Loading: ${loadingText || 'Processing'}` }
          : {})}
      >
        {/*
          While loading, the wrapper hides any nested icon and the spinner takes
          its slot (Button sizes svgs to size-4 with gap-2), so icon buttons
          don't shift. `contents` keeps the wrapper transparent to flex layout.
        */}
        {loading && (
          <Loader2
            className='h-4 w-4 animate-spin motion-reduce:animate-none'
            aria-hidden='true'
          />
        )}
        <span className={cn('contents', loading && '[&_svg]:hidden')}>
          {buttonContent}
        </span>
      </Button>
    );
  }
);

/**
 * `useTransition` keeps the spinner visible until the destination route's
 * server payload resolves. Router hooks live here, not in the base, so they
 * only run for buttons that navigate.
 */
const NavActionButton = React.forwardRef<HTMLButtonElement, ActionButtonProps>(
  function NavActionButton(
    { navigateTo, navigateReplace, loading, onClick, ...props },
    ref
  ) {
    const router = useRouter();
    const [isPending, startTransition] = useTransition();

    const handleClick = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        // Honor any caller-provided onClick first (e.g. analytics, preventDefault).
        onClick?.(event);
        if (event.defaultPrevented || !navigateTo) return;
        startTransition(() => {
          if (navigateReplace) {
            router.replace(navigateTo);
          } else {
            router.push(navigateTo);
          }
        });
      },
      [router, navigateTo, navigateReplace, onClick]
    );

    return (
      <ActionButtonBase
        {...props}
        ref={ref}
        loading={loading || isPending}
        onClick={handleClick}
      />
    );
  }
);

export const ActionButton = React.forwardRef<
  HTMLButtonElement,
  ActionButtonProps
>(function ActionButton(props, ref) {
  if (props.navigateTo && !props.asChild) {
    return <NavActionButton {...props} ref={ref} />;
  }
  return <ActionButtonBase {...props} ref={ref} />;
});

export type { ActionButtonProps };

export const ActionButtonVariants = {
  CREATE: 'create' as const,
  UPDATE: 'update' as const,
  DELETE: 'delete' as const,
  SUBMIT: 'submit' as const,
  CANCEL: 'cancel' as const
} as const;
